import { useEffect, useState, useCallback } from "react";
import { Type, Minus, Plus } from "lucide-react";

/**
 * App-wide text-zoom controls.
 *
 * Scales the root `<html>` font-size — every Tailwind `rem`-based size
 * (text-sm, p-4, h-12, etc.) inherits the scale, so spacing stays
 * proportional and layouts don't break. Pixel-locked icon sizes stay
 * fixed at their visual baseline, which is what we want — icons should
 * not look ridiculous at 130%.
 *
 * Three sizes covers the practical demo population (younger / standard
 * / older eyes). Power users can still use Ctrl/⌘ +/− which compounds
 * over this setting.
 *
 * The setting persists in localStorage so a user only sets it once.
 */
const LS_KEY = "erio_text_scale";
const DEFAULT_SCALE = 1.0;
const STEPS = [0.9, 1.0, 1.15, 1.3]; // 90 / 100 / 115 / 130 %

function clamp(s) {
  return STEPS.reduce((closest, v) => (Math.abs(v - s) < Math.abs(closest - s) ? v : closest), STEPS[1]);
}

function applyScale(scale) {
  // Drive a CSS variable rather than a direct font-size so any opt-out
  // component can ignore it via `font-size: 16px !important` if needed.
  const root = document.documentElement;
  root.style.setProperty("--erio-text-scale", String(scale));
  // Tailwind's base is 16px = 1rem; we set the actual root font-size so
  // every rem-based utility class inherits the scale.
  root.style.fontSize = `${16 * scale}px`;
  root.setAttribute("data-text-scale", String(scale));
}

export function useTextScale() {
  const [scale, setScale] = useState(() => {
    try {
      const v = parseFloat(localStorage.getItem(LS_KEY));
      return v && STEPS.includes(clamp(v)) ? clamp(v) : DEFAULT_SCALE;
    } catch {
      return DEFAULT_SCALE;
    }
  });

  useEffect(() => {
    applyScale(scale);
    try {
      localStorage.setItem(LS_KEY, String(scale));
    } catch {
      // localStorage disabled / quota → fail soft, in-session scale still works
    }
  }, [scale]);

  const dec = useCallback(() => {
    setScale((s) => {
      const i = STEPS.indexOf(clamp(s));
      return i > 0 ? STEPS[i - 1] : s;
    });
  }, []);

  const inc = useCallback(() => {
    setScale((s) => {
      const i = STEPS.indexOf(clamp(s));
      return i < STEPS.length - 1 ? STEPS[i + 1] : s;
    });
  }, []);

  const reset = useCallback(() => setScale(DEFAULT_SCALE), []);

  return { scale, dec, inc, reset, isMin: scale <= STEPS[0], isMax: scale >= STEPS.at(-1) };
}

export function TextScaleControls() {
  const { scale, dec, inc, reset, isMin, isMax } = useTextScale();
  const pct = Math.round(scale * 100);

  return (
    <div
      className="flex items-center gap-0.5 rounded-full border border-white/15 bg-white/5 px-1 py-0.5"
      data-testid="text-scale-controls"
      role="group"
      aria-label="Text size"
    >
      <button
        type="button"
        onClick={dec}
        disabled={isMin}
        title="Smaller text"
        aria-label="Smaller text"
        data-testid="text-scale-dec"
        className="rounded-full p-1.5 text-white/80 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent"
      >
        <Minus size={12} />
      </button>
      <button
        type="button"
        onClick={reset}
        title={`Text size · ${pct}% — click to reset`}
        aria-label={`Text size ${pct} percent — click to reset to 100%`}
        data-testid="text-scale-reset"
        className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold text-white/80 hover:bg-white/10"
      >
        <Type size={11} />
        <span>{pct}%</span>
      </button>
      <button
        type="button"
        onClick={inc}
        disabled={isMax}
        title="Larger text"
        aria-label="Larger text"
        data-testid="text-scale-inc"
        className="rounded-full p-1.5 text-white/80 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent"
      >
        <Plus size={12} />
      </button>
    </div>
  );
}
